<template>
  <div class="step-three">
    <vue-tabs ref="tabsContainer" @input="handleTabInput">
      <v-tab v-for="(adset, index) in value.adsets" :key="'adset-' + index" :tabKey="'adset-' + index" :hasError="value.adsets[index].hasError" :title="'adset-' + index">
        <!-- Start title -->
        <div slot="title">
          <span class="align-middle pl-3 adset-title">
            {{ value.adsets[index].name === '' ? ('Ad Set ' + (index + 1)) : value.adsets[index].name }}
          </span>

            <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer align-middle pl-3 mt-1">
              <vs-icon icon-pack="speakol" icon="speakol-dots" class="adset-icon"/>
              <vs-dropdown-menu class="vx-navbar-dropdown">
                <ul style="min-width: 9rem;">
                  <li id="create-campaign-s3-duplicate-adset-button" v-if="value.adsets.length < 4" class="flex py-2 px-4 cursor-pointer hover:bg-gray-200 hover:text-black" @click="duplicateAdset(index)">
                    <vs-icon class="duplicate-adset-icon" icon-pack="speakol" icon="speakol-duplicate"></vs-icon>
                    <span class="ml-2 dropdown-actions">{{ $t("Duplicate Ad Set") }}</span>
                  </li>
                  <li id="create-campaign-s3-remove-adset-button" v-if="value.adsets.length > 1 && value.adsets[index].id === 0" class="flex py-2 px-4 cursor-pointer hover:bg-gray-200 hover:text-black" @click="removeAdset(index, 'confirm')">
                    <vs-icon icon-pack="speakol" icon="speakol-remove" svgClasses="w-5 h-5" style="color: #e02020;"/>
                    <span class="ml-2 dropdown-actions">{{ $t("Remove Ad Set") }}</span>
                  </li>

                  <li v-if="!(value.adsets.length < 4) && !(value.adsets.length > 1 && value.adsets[index].id === 0)" class="flex py-2 px-4 cursor-pointer hover:bg-gray-200 hover:text-black">
                    <vs-icon icon-pack="speakol" icon="speakol-alert-circle" svgClasses="w-5 h-5"/>
                    <span class="ml-2">{{ $t("No Actions") }}</span>
                  </li>
                </ul>
              </vs-dropdown-menu>
            </vs-dropdown>
        </div>
        <!-- End title -->

        <!-- Start content -->
        <AdsetContent v-model="value.adsets[index]" :advertising-channel="advertisingChannel" :adsetIndex="index" :industry_id="industry_id" :objective="objective" :type="type" :serverErrors="serverErrors" :editing="editing && value.adsets[index].id > 0" @fireTrafficEstimator="fireTrafficEstimator" @adsetCountriesChanged="adsetCountriesChanged"/>
        <!-- End content -->

        <vs-popup class="holamundo" :title="$t('Confirmation')" :active="removableAdsetIndex === index">
          <p class="mb-10">{{ $t('Are you sure you want to remove') }} {{ `“${adset.name}”` }} ?</p>
          <div class="text-center md:text-right">
            <vs-button id="create-campaign-s3-remmove-adset-popup-close-button" class="shadow-none mr-4" color="rgb(200, 200, 200)" textColor="#000" type="filled" @click="removeAdset(index, 'cancel')">
              {{ $t('Close') }}
            </vs-button>

            <vs-button id="create-campaign-s3-remove-adset-popup-confirm-button" color="primary" type="filled" @click="removeAdset(index, 'remove')">
              {{ $t('Confirm') }}
            </vs-button>
          </div>
        </vs-popup>
      </v-tab>

      <v-tab :disabled="value.adsets.length === 4" :isTab="false">
        <div id="create-campaign-s3-add-adset-button" class="add-adset-button" @click="addAdset()" slot="title">
          <span class="align-middle">
            <vs-icon class="ad-adset-icon" :class="{'text-gray-500': value.adsets.length === 4}" icon-pack="speakol" icon="speakol-add"></vs-icon>
          </span>
          <span class="ad-adset-span align-middle" :class="{'text-gray-500': value.adsets.length === 4}">
            {{ $t("Add Ad Set") }}
          </span>
        </div>
      </v-tab>
    </vue-tabs>
  </div>
</template>

<script>
import {
  VueTabs,
  VTab
} from '@/components/vue-nav-tabs/vue-nav-tabs.js'

import '@/components/vue-nav-tabs/nav-tabs.scss'

import AdsetContent from '@/views/campaigns/components/steps/components/AdsetContent/AdsetContent.vue'

import { get, cloneDeep, debounce } from 'lodash'
import { ignoreTargeting } from '@/utils/IgnoreTargeting.js'

const ESTIMATOR_DEBOUNCE_TIME = 2000

export default {
  props: {
    value: {
      required: true,
      type: Object
    },
    serverErrors: {
      required: true
    },
    advertisingChannel: {
      type: Object,
      required: true
    },
    objective: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    industry_id: {
      type: Number,
      required: true
    },
    editing: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  components: {
    VueTabs,
    VTab,
    AdsetContent
  },
  computed: {
    devices () {
      return this.$store.getters['campaign/targetingLookups'].devices
    }
  },
  watch: {
    'value': {
      handler (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    'serverErrors': {
      handler () {
        this.checkAdsetsErrors()
      },
      deep: true
    }
  },
  data () {
    return {
      campaignTypes: this.$constants.CAMPAIGN_TYPES_NAMES,
      removableAdsetIndex: null,
      activeAdsetIndex: 0,
      deviceTypes: this.$constants.DEVICES_TYPES,
      fireTrafficEstimator: (adsetIndex) => {
        if (adsetIndex !== this.activeAdsetIndex) {
          return
        }

        this.prepareTrafficEstimatorFire(adsetIndex)
      },
      prepareTrafficEstimatorFire: debounce((adsetIndex) => {
        if (adsetIndex !== this.activeAdsetIndex) {
          return
        }

        this.setTrafficEstimator(adsetIndex)
      }, ESTIMATOR_DEBOUNCE_TIME)
    }
  },
  mounted () {
    // if the url param has ?adset=x will activate the tab with id x
    if (this.$route.query.adset || 0) {
      setTimeout(() => {
        const indexToActivate = this.value.adsets.findIndex(i => i.id === parseInt(this.$route.query.adset))
        if (indexToActivate > -1) {
          this.$refs.tabsContainer.findTabAndActivate(`adset-${indexToActivate}`)
        }
      }, 0)
    }
  },
  methods: {
    addAdset (adset = null) {
      if (this.value.adsets.length >= 4) {
        return
      }

      if (adset === null) {
        adset = cloneDeep(this.$store.getters['campaign/defaultAdsetObject'])
        adset.name = `Ad Set ${  this.value.adsets.length + 1}`
      }

      this.value.adsets.push(adset)

      const tabsLength = this.value.adsets.length,
        newTabIndex = tabsLength - 1
      setTimeout(() => {
        this.$refs.tabsContainer.findTabAndActivate(`adset-${newTabIndex}`)
      }, 0)
      this.$gtag.event('Step3_AddNewAdset')
    },
    removeAdset (index, actionType) {
      if (actionType === 'confirm') {
        this.removableAdsetIndex = index
        return
      }

      if (actionType === 'cancel') {
        this.removableAdsetIndex = null
        return
      }

      if (actionType === 'remove') {
        if (this.value.adsets.length === 1) {
          return
        }

        this.removableAdsetIndex = null

        setTimeout(() => {
          this.value.adsets.splice(index, 1)

          this.$refs.tabsContainer.findTabAndActivate(`adset-${  index  > 0 ? index - 1 : index}`)
        }, 0)
      }
    },
    duplicateAdset (index) {
      // if (this.value.adsets[index].hasError) {
      //   return
      // }

      const cloned = cloneDeep(this.value.adsets[index])

      cloned.id = 0 // thats a new adset indicator
      cloned.name += ' Copy'
      cloned.is_duplicating = true
      cloned.new_audience_name = ''
      cloned.new_audience = true
      cloned.audience_id = 0

      cloned.budget.custom_bids = []

      cloned.ads = cloned.ads.map((ad) => {
        ad.id = 0
        ad.show_audience_suggestions = true

        return ad
      })

      this.addAdset(cloned)
    },
    checkAdsetsErrors () {
      const errors = []
      const adsetsHasErrors = []

      this.serverErrors.items.forEach(item => {
        errors.push(item.field)
      })

      this.errors.items.forEach(item => {
        errors.push(item.field)
      })

      // add the errors to adsets has errors
      this.value.adsets.forEach((adset, index) => {
        errors.forEach(e => {
          if (e.includes(`.adsets.${index}`)) {
            this.value.adsets[index].hasError = true
            adsetsHasErrors.push(adset.id)
          }
        })
      })

      // remove the errors from adsets that dose not has errors
      this.value.adsets.forEach((adset, index) => {
        if (!adsetsHasErrors.includes(adset.id)) {
          this.value.adsets[index].hasError = false
        }
      })
    },
    handleTabInput (tabTitle) {
      const tabIndex = Number(tabTitle.replace('adset-', ''))

      this.activeAdsetIndex = tabIndex

      this.prepareTrafficEstimatorFire(tabIndex)

      this.$emit('setActiveAdsetIndex', tabIndex)
    },
    setEstimation (estimation) {
      this.$emit('passEstimationToRoot', estimation)
    },
    async setTrafficEstimator (adsetIndex) {
      const adset = cloneDeep(get(this.value, `adsets[${ adsetIndex }]`, {})),
        cities = get(adset, 'locations.cities', []),
        countries = get(adset, 'locations.countries', []),
        domains = get(adset, 'target_platforms.websites', []),
        apps_domains = get(adset, 'target_platforms.apps', []),
        content_categories = get(adset, 'content_categories', []),
        deviceTypes = this.deviceTypes,
        devices = get(adset, 'target_devices', []).map(function (deviceId) {
          return deviceTypes[deviceId]
        })
      // check estimation criteria elements
      if ((!cities.length && !countries.length) || (!ignoreTargeting(this.objective, this.type) && !domains.length && !apps_domains.length) || !content_categories.length || !devices.length) {
        this.value.adsets[adsetIndex].estimatedPageViews = 0
        this.setEstimation({
          adsetIndex
        })
        return
      }

      // get traffic estimation based on criteria
      try {
        const data = {
          cities,
          countries,
          domains,
          apps_domains,
          content_categories,
          devices
        }

        const response = await window.axios.post('/campaigns/page-views', data, { hideLoader: true }),
          success = get(response, 'data.success', false),
          payload = get(response, 'data.payload.payload', {}),
          allPageViews = get(payload, 'all_page_views', 100000000),
          targetedPageViews = get(payload, 'targeted_page_views', 0)
        if (!success) {
          return
        }

        this.value.adsets[adsetIndex].allPageViews = allPageViews
        this.value.adsets[adsetIndex].estimatedPageViews = targetedPageViews
        this.setEstimation({
          adsetIndex
        })
      } catch (error) {
        if (!window.axios) {
          return
        }

        window.axios.sendHandledSentryException(error)
      }
    }, 
    adsetCountriesChanged(data) {
      if (this.value.adsets[data.adsetIndex] && this.value.adsets[data.adsetIndex].budget.budget_value ) {
        this.$emit('adsetCountriesChanged', data)
      }
    },
  }
}
</script>

<style lang="scss">
  .add-adset-button {
    width: 110px;
    position: relative;
    top: 3px;
  }

  .tab-has-error {
    .adset-title,
    .adset-icon {
      color: #e02020;
    }

    a.tabs__link {
      border-bottom: 2px solid #e02020 !important;
    }
  }

  .ad-adset-icon {
    margin-right: 5px;
    position: relative;
    top: 2px;
  }

  .duplicate-adset-icon {
    font-size: 17px !important;
    position: relative;
    top: 2px;
  }

  .con-vs-dropdown--menu {
    position: absolute;
    z-index: 9999;
  }

  @media (max-width: 576px) {
    .dropdown-actions {
      width: max-content;
    }

    .ad-adset-span {
      display: none;
    }

    .add-adset-button {
      width: auto;
    }
  }
</style>
